import { ReactElement } from 'react';
import { observer } from 'mobx-react';
import isNil from 'lodash/isNil';
import { roundNumber } from '@cutwise/utils';

import { useConfigurationStore } from '@/common/stores/ConfigurationStore';
import { METRIC_DATA_DECORATOR_DEFAULT, METRIC_DATA_DECORATOR_FANCY } from '@/metric/constants/dataDecorators';
import { RATIO_THICK } from '@/metric/constants/ratio';
import BrightnessMetric from '@/metric/entity/BrightnessMetric';
import DeadZoneMetric from '@/metric/entity/DeadZoneMetric';
import FireMetric from '@/metric/entity/FireMetric';
import IntegralMetric from '@/metric/entity/IntegralMetric';
import IntegralV2Metric from '@/metric/entity/IntegralV2Metric';
import ScintillationMetric from '@/metric/entity/ScintillationMetric';
import SymmetryMetric from '@/metric/entity/SymmetryMetric';

import MetricBar from '../MetricBar/MetricBar';

interface OptMetricBarProps {
  metric: Nullable<
    | FireMetric
    | ScintillationMetric
    | BrightnessMetric
    | IntegralMetric
    | IntegralV2Metric
    | SymmetryMetric
    | DeadZoneMetric
  >;
  type: string;
  children?: (metricBar: ReactElement, value: Nullable<string>) => ReactElement;
  ratio?: number;
  cutShapeId?: Nullable<number>;
  metricDataDecorator?: string;
  rgFieldName?: string;
  valFieldName?: string;
}

function OptMetricBar(props: OptMetricBarProps) {
  const {
    metric,
    children,
    ratio = RATIO_THICK,
    cutShapeId,
    rgFieldName = 'rg',
    valFieldName = 'val',
    metricDataDecorator = METRIC_DATA_DECORATOR_DEFAULT,
    type,
  } = props;
  const { scaleBaseUri } = useConfigurationStore();

  function renderMetricBar() {
    const params = new URLSearchParams({
      ratio: `${ratio}`,
    });

    if (metric) {
      params.append('rg', `${roundNumber(Number(metric[rgFieldName as keyof typeof metric]), 2)}`);
      params.append('d', `${roundNumber(metric.d, 2)}`);

      if (cutShapeId && metricDataDecorator === METRIC_DATA_DECORATOR_FANCY) {
        params.append('sh', `${cutShapeId}`);
      }
    }

    return <MetricBar ratio={ratio} svg={`${scaleBaseUri}/line/${type}?${params.toString()}`} />;
  }

  const metricValNumber =
    metric && !isNil(metric[valFieldName as keyof typeof metric])
      ? Number(metric[valFieldName as keyof typeof metric]).toFixed(2)
      : null;

  return children ? children(renderMetricBar(), metricValNumber) : renderMetricBar();
}

export default observer(OptMetricBar);
